import type { Config, DashboardWidgets } from '../types';

const CACHE_KEY_PREFIX = 'smartchart:widget-config:';

/**
 * SessionStorage key for widget config, scoped by dashboard context.
 *
 * - Patient chart (`patientNumber` set): `…:{agency}:{userId}:patient:{patientNumber}`
 * - Clinical (no patient): `…:{agency}:{userId}:clinical`
 *
 * Scoping avoids sharing one entry across patient vs clinical, and across patients.
 */
export function getWidgetConfigCacheKey(
  agency: string | undefined,
  userId: string | undefined,
  patientNumber?: string | null,
): string | null {
  if (!agency || !userId) {
    return null;
  }
  if (patientNumber) {
    return `${CACHE_KEY_PREFIX}${agency}:${userId}:patient:${patientNumber}`;
  }
  return `${CACHE_KEY_PREFIX}${agency}:${userId}:clinical`;
}

function sectionForCacheKey(
  cacheKey: string,
  widgets: Config['widgets'],
): DashboardWidgets | undefined {
  if (/:patient:[^:]+$/.test(cacheKey)) {
    return widgets.patientDashboard;
  }
  if (/:clinical$/.test(cacheKey)) {
    return widgets.clinicalDashboard;
  }
  return undefined;
}

export function readWidgetConfigCache(cacheKey: string): Config | null {
  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Config;
    const section = sectionForCacheKey(cacheKey, parsed?.widgets);
    // Accept empty layout arrays; reject missing section or non-array layout.
    if (!section || !Array.isArray(section.layout)) {
      return null;
    }
    return {
      ...parsed,
      configSource: 'cache',
    };
  } catch {
    return null;
  }
}

export function writeWidgetConfigCache(cacheKey: string, config: Config): void {
  try {
    sessionStorage.setItem(cacheKey, JSON.stringify(config));
  } catch {
    // Quota or private mode — ignore; dashboard still works without cache.
  }
}

export function areWidgetConfigsEqual(a: Config, b: Config): boolean {
  return JSON.stringify(a.widgets) === JSON.stringify(b.widgets);
}
